import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { hiringLinksAPI, type HiringStoreOption } from '../../services/api';
import { hiringErrorText } from './hiringText';
import { LINK_WORDS } from './hiringText.dashboard';

/** A store as "Link an opening" holds it: enough to send and to name it. */
export interface LinkStore {
  storeId: string;
  storeName: string | null;
  storeCode: string | null;
  city?: string | null;
}

/** "Sample Mart 1 (SM-01)", and the city after it when known. */
export const linkStoreLabel = (store: LinkStore, withCity = true): string => {
  const name = (store.storeName ?? '').trim();
  const code = (store.storeCode ?? '').trim();
  const named = name && code ? `${name} (${code})` : name || code || 'A store';
  const city = withCity ? (store.city ?? '').trim() : '';
  return city ? `${named} · ${city}` : named;
};

const SEARCH_WAIT_MS = 300;
/** The server's own limit on the text (hiring-link-search.logic PERSON_SEARCH_MAX). */
const SEARCH_MAX = 50;
/** How close to the end of the list (px) a scroll asks for the next page. */
const NEAR_END_PX = 48;

/**
 * "Store (optional)" in Link an opening, opened from the Hiring dashboard: the stores within
 * this person's reach, searched on the server by name or code, with "No store" as the first
 * choice. Nothing is cut off: the server answers a page (50) at a time, and the list asks for
 * the next as it is scrolled to its end (or stepped past its last store with the arrow keys),
 * until there is no more. Nothing is greyed: a link only needs the store to be the project's,
 * which the server checks.
 *
 * The list is a fixed-position panel over the window (as SearchableSelect's), so a long
 * list is never cut off by the window's own scrolling.
 */
export function LinkStorePicker({
  id, projectId, value, onChange, disabled = false, onRefused,
}: {
  id?: string;
  projectId: number;
  value: LinkStore | null;
  onChange: (store: LinkStore | null) => void;
  disabled?: boolean;
  /** A refusal the owner acts on (hiring switched off). Returns true when it did. */
  onRefused?: (err: unknown) => boolean;
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [stores, setStores] = useState<HiringStoreOption[] | null>(null);
  const [total, setTotal] = useState(0);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState<{ left: number; width: number; top?: number; bottom?: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const asked = useRef(0);
  const refused = useRef(onRefused);
  refused.current = onRefused;

  // A fresh search each time it opens.
  useEffect(() => {
    if (!open) {
      setTyped('');
      setActive(0);
    }
  }, [open]);

  // The stores, read when the list opens and as the person types (after a pause).
  const q = typed.trim().replace(/\s+/g, ' ').slice(0, SEARCH_MAX);
  useEffect(() => {
    if (!open) return undefined;
    const req = ++asked.current;
    setError(null);
    // A new search drops what an earlier one found: nothing older is left to pick.
    setStores(null);
    setNextOffset(null);
    setLoadingMore(false);
    const timer = setTimeout(() => {
      Promise.resolve()
        .then(() => hiringLinksAPI.linkStores(projectId, q || undefined))
        .then((res) => {
          if (req !== asked.current) return;
          const data = res?.data?.data;
          const found = Array.isArray(data?.stores) ? data.stores : [];
          setStores(found);
          setTotal(typeof data?.total === 'number' ? data.total : found.length);
          setNextOffset(typeof data?.nextOffset === 'number' ? data.nextOffset : null);
          setActive(0);
        })
        .catch((err: unknown) => {
          if (req !== asked.current) return;
          if (refused.current?.(err)) return;
          setStores(null);
          setError(hiringErrorText(err, LINK_WORDS.storesFailed));
        });
    }, q ? SEARCH_WAIT_MS : 0);
    return () => {
      clearTimeout(timer);
      asked.current += 1;
    };
  }, [open, q, projectId]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const update = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r) return;
      const H = 340;
      const below = window.innerHeight - r.bottom;
      const openUp = below < H && r.top > below;
      setPos({ left: r.left, width: r.width, ...(openUp ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Closes the list, not the window under it.
      e.stopPropagation();
      setOpen(false);
      btnRef.current?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  /** The next page, added to the list: at its end, once per page, until the server says there is no more. */
  const loadMore = () => {
    if (nextOffset == null || loadingMore || stores === null) return;
    const req = asked.current;
    setLoadingMore(true);
    Promise.resolve()
      .then(() => hiringLinksAPI.linkStores(projectId, q || undefined, nextOffset))
      .then((res) => {
        if (req !== asked.current) return;
        const data = res?.data?.data;
        const found = Array.isArray(data?.stores) ? data.stores : [];
        setStores((was) => {
          const seen = new Set((was ?? []).map((x) => x.storeId));
          return [...(was ?? []), ...found.filter((x) => !seen.has(x.storeId))];
        });
        setNextOffset(typeof data?.nextOffset === 'number' ? data.nextOffset : null);
      })
      .catch((err: unknown) => {
        if (req !== asked.current) return;
        if (refused.current?.(err)) return;
        // What was read stays; scrolling to the end asks again.
        setError(hiringErrorText(err, LINK_WORDS.storesFailed));
      })
      .finally(() => {
        if (req === asked.current) setLoadingMore(false);
      });
  };

  // "No store" first, then the stores found.
  const choices: Array<LinkStore | null> = [null, ...(stores ?? []).map((s) => ({
    storeId: s.storeId, storeName: s.storeName, storeCode: s.storeCode, city: s.city,
  }))];

  const pick = (store: LinkStore | null) => {
    onChange(store);
    setOpen(false);
    btnRef.current?.focus();
  };

  const onSearchKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      // At the last store, the arrow asks for the next page, as scrolling to the end does.
      if (active >= choices.length - 2) loadMore();
      setActive((i) => Math.min(i + 1, choices.length - 1));
    }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (active < choices.length) pick(choices[active]);
    }
  };

  const shownCount = stores?.length ?? 0;
  return (
    <div className="relative flex items-center">
      <button
        id={id}
        ref={btnRef}
        type="button"
        role="combobox"
        aria-label={LINK_WORDS.store}
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={disabled}
        onClick={() => { if (!disabled) setOpen((o) => !o); }}
        className={`flex h-9 w-full min-w-0 items-center justify-between gap-2 rounded-lg border border-gray-300 pl-3 ${value ? 'pr-16' : 'pr-3'} text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 ${disabled ? 'cursor-not-allowed bg-gray-50 text-gray-400' : 'bg-white'}`}
      >
        <span className={`truncate text-left ${value ? 'text-gray-900' : 'text-gray-400'}`}>
          {value ? linkStoreLabel(value) : LINK_WORDS.noStore}
        </span>
        <ChevronDown aria-hidden="true" className={`h-4 w-4 flex-shrink-0 text-gray-400 ${value ? 'absolute right-3' : ''}`} />
      </button>
      {value && !disabled && (
        <button
          type="button"
          aria-label={`${LINK_WORDS.noStore}: clear the store`}
          onClick={() => onChange(null)}
          className="absolute right-8 rounded p-0.5 text-gray-400 hover:text-gray-600"
        >
          <X aria-hidden="true" className="h-4 w-4" />
        </button>
      )}
      {open && pos && createPortal(
        <div
          ref={popRef}
          style={{ position: 'fixed', left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, zIndex: 140 }}
          className="rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          <div className="border-b border-gray-100 p-2">
            <div className="relative">
              <Search aria-hidden="true" className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                aria-label={LINK_WORDS.storeSearch}
                value={typed}
                maxLength={SEARCH_MAX}
                onChange={(e) => setTyped(e.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder={LINK_WORDS.storeSearch}
                autoFocus
                autoComplete="off"
                className="h-8 w-full rounded border border-gray-200 pl-7 pr-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>
          <div
            role="listbox"
            aria-label={LINK_WORDS.store}
            aria-busy={loadingMore || undefined}
            onScroll={(e) => {
              const el = e.currentTarget;
              if (el.scrollTop + el.clientHeight >= el.scrollHeight - NEAR_END_PX) loadMore();
            }}
            className="max-h-72 overflow-y-auto py-1"
          >
            {choices.map((store, i) => {
              const on = store ? value?.storeId === store.storeId : value === null;
              return (
                <div
                  key={store?.storeId ?? 'none'}
                  role="option"
                  aria-selected={on}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(store)}
                  className={`flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5 text-sm ${on ? 'bg-primary-50 text-primary-700' : i === active ? 'bg-gray-50 text-gray-800' : 'text-gray-700'}`}
                >
                  <span className={`truncate ${store ? '' : 'text-gray-500'}`}>{store ? linkStoreLabel(store) : LINK_WORDS.noStore}</span>
                  {on && <Check aria-hidden="true" className="h-4 w-4 flex-shrink-0 text-primary-600" />}
                </div>
              );
            })}
            {loadingMore && <div role="status" className="px-3 py-1.5 text-sm text-gray-500">{LINK_WORDS.storesMore}</div>}
          </div>
          <div className="border-t border-gray-100 px-3 py-1.5 text-xs text-gray-500">
            {error ?? (stores === null ? LINK_WORDS.storesLoading : shownCount === 0 ? LINK_WORDS.storesNone : LINK_WORDS.storesShown(total))}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

export default LinkStorePicker;
